/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";
import { useProgramData } from "@hooks/data/useProgramData";
import { IonHeader, IonPage, IonToolbar, IonBackButton, IonButton, IonSpinner, IonContent } from "@ionic/react";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import { IonButtons } from "@ionic/react";
import HeaderTitle from "@components/common/HeaderTitle";
import { Icon_Refresh } from "@assets/images";
import { IUser, IUserProgramCertificate, ProgramCertificate, ProgramData, Required } from "@interfaces/Interfaces";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { userAtom } from "@state/State";
import { userRolesSelector } from "@state/UserState";
import * as ProgramUtils from "@utils/programUtils";
import _ from "lodash";
import ProgramCertificateButton from "@components/programs/ProgramCertificateButton";
import CertificateModal from "@components/programs/CertificateModal";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import classNames from "classnames";

const ProgramCertificatesPage: React.FC = () => {
  const params = useParams<{ programId: string }>();

  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();
  const { userPrograms } = useProgramData();

  const [certificateModalVisible, setCertificateModalVisible] = useState<boolean>(false);
  const [selectedCertificate, setSelectedCertificate] = useState<ProgramCertificate | any>(null);
  const [programCertificates, setProgramCertificates] = useState<IUserProgramCertificate[] | any[]>([]);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);

  const program: ProgramData = userPrograms.find((item) => item.ID === params.programId)!;
  const programRoles = allUserRoles.find((role) => role.programID === params.programId);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }
  const certificatesForRole =
    (program && (program as ProgramData)?.Roles?.find((role) => role.Name === userRoles[0])?.Certificates) ?? [];

  const sortedCertificatesForRole = _.sortBy(certificatesForRole, "Name");

  const mandatoryCertificates = sortedCertificatesForRole.filter(
    (certificate) => certificate.Required === Required.Mandatory
  );
  const optionalCertificates = sortedCertificatesForRole.filter(
    (certificate) => certificate.Required === Required.Optional
  );

  useEffect(() => {
    const programOrder = (item: IUserProgramCertificate): number => {
      const certificateName = item?.certificate?.certificateType;
      const mappedCertificate = program?.Certificates?.find(
        (_cert: ProgramCertificate) => _cert.Name.toUpperCase() === certificateName?.toUpperCase()
      );

      return mappedCertificate?.Required === Required.Mandatory ? 2 : 1;
    };

    const getUserProgramCertificates = (): void => {
      if (user.contents) {
        let _programCertificates: IUserProgramCertificate[] = user.contents?.programCertificates ?? [];
        _programCertificates = _programCertificates.filter(
          (item: IUserProgramCertificate) => item.programData.programName === program?.Name
        );

        setProgramCertificates(
          _.orderBy(_programCertificates, [programOrder, (item) => item.programData.certificateType], ["desc", "asc"])
        );
      }
    };

    if (user.state === "hasValue" && program) {
      getUserProgramCertificates();
    }
  }, [program, user]);

  const closeCertificateModal = (): void => {
    setSelectedCertificate(null);
    setCertificateModalVisible(false);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{program?.CertificateListHeader ?? "Certificates"}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-[20px] w-[44px] text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <CertificateModal
          visible={certificateModalVisible}
          closeModal={() => closeCertificateModal()}
          certificate={selectedCertificate}
          programCertificates={programCertificates}
          program={program}
          role={userRoles[0]}
        />
        <ProgramInfoCard
          title={program?.CertificateListHeader ?? "Certificates"}
          subTitle={program.CertificatePageHeader ?? undefined}
          isHTMLSubtitle
        >
          {mandatoryCertificates?.length > 0 && (
            <>
              <div className="mb-2 mt-4 text-acc-13px font-acc-600 text-grey-90">{"Mandatory"}</div>
              {mandatoryCertificates?.map((certificate: ProgramCertificate) => (
                <ProgramCertificateButton
                  certificate={certificate}
                  buttonPressed={() => {
                    setSelectedCertificate(certificate);
                    setCertificateModalVisible(true);
                  }}
                  key={certificate.ID}
                  programCertificates={programCertificates}
                  role={userRoles[0]}
                  programID={program.ID}
                  defaultButtonText={program.CertificateTerminology}
                />
              ))}
            </>
          )}
          {optionalCertificates?.length > 0 && (
            <>
              <div
                className={classNames("mb-2 text-acc-13px font-acc-600 text-grey-90", {
                  "mt-4": mandatoryCertificates?.length > 0,
                })}
              >
                {"Optional"}
              </div>
              {optionalCertificates?.map((certificate: ProgramCertificate) => (
                <ProgramCertificateButton
                  certificate={certificate}
                  buttonPressed={() => {
                    setSelectedCertificate(certificate);
                    setCertificateModalVisible(true);
                  }}
                  key={certificate.ID}
                  programCertificates={programCertificates}
                  role={userRoles[0]}
                  programID={program.ID}
                  defaultButtonText={program.CertificateTerminology}
                />
              ))}
            </>
          )}
        </ProgramInfoCard>
      </IonContent>
    </IonPage>
  );
};

export default ProgramCertificatesPage;
